"use client";

import { useEffect, useState, useCallback, useMemo } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import type { DialogNode, TopicVocabulary } from "@/lib/dialog-data";
import { buildDictionary, type WordDefinition } from "@/components/word-lookup";
import { SpeakerButton } from "@/components/speaker-button";
import { useTTS } from "@/lib/use-tts";
import { useLanguage } from "@/lib/language-context";
import { useTranslations } from "@/lib/use-translations";
import { useSettings } from "@/lib/settings-context";
import api from "@/lib/api/client";

interface PrefetchedVocab {
  words: Map<string, PracticeWord>;
  expressions: PracticeExpression[];
  phoneticTranscription?: string;
}

interface NodePracticeModalProps {
  node: DialogNode;
  vocabulary?: TopicVocabulary;
  prefetchedVocab?: PrefetchedVocab;
  prefetchPromise?: Promise<PrefetchedVocab>;
  onClose: () => void;
}

interface PracticeWord {
  word: string;
  baseForm: string;
  translation: string;
  pronunciation?: string;
  vocalizationTip?: string; // How to pronounce using native language sounds
}

interface PracticeExpression {
  expression: string;
  translation: string;
  literal?: string;
  components: Array<{ word: string; meaning: string }>;
}

// Extract all unique words from text
function extractAllWords(text: string): string[] {
  const words = text
    .split(/\s+/)
    .map((w) => w.replace(/[.,!?;:'"()¿¡«»„"]/g, "").trim())
    .filter((w) => w.length >= 1);

  const seen = new Set<string>();
  const result: string[] = [];

  for (const word of words) {
    const normalized = word.toLowerCase();
    if (seen.has(normalized)) continue;
    seen.add(normalized);
    result.push(word);
  }

  return result;
}

// Match words against local vocabulary dictionary
function matchWordsToVocab(
  words: string[],
  dictionary: Map<string, WordDefinition>
): { matched: PracticeWord[]; unmatched: string[] } {
  const matched: PracticeWord[] = [];
  const unmatched: string[] = [];

  for (const word of words) {
    const normalized = word.toLowerCase();
    const def = dictionary.get(normalized);
    if (def) {
      matched.push({
        word: word,
        baseForm: def.word,
        translation: def.translation,
        pronunciation: def.pronunciation,
      });
    } else {
      unmatched.push(word);
    }
  }

  return { matched, unmatched };
}

export function NodePracticeModal({
  node,
  vocabulary,
  prefetchedVocab,
  prefetchPromise,
  onClose,
}: NodePracticeModalProps) {
  const { targetLanguage, nativeLanguage } = useLanguage();
  const { userProfile } = useSettings();
  const t = useTranslations();
  const { speak, stop, state: ttsState } = useTTS({
    lang: targetLanguage.code,
  });

  const [practiceWords, setPracticeWords] = useState<PracticeWord[]>([]);
  const [practiceExpressions, setPracticeExpressions] = useState<PracticeExpression[]>([]);
  const [phoneticTranscription, setPhoneticTranscription] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [speakingText, setSpeakingText] = useState<string | null>(null);
  const [currentPhraseIndex, setCurrentPhraseIndex] = useState(0);
  const [activeTab, setActiveTab] = useState<"words" | "phrases">("words");

  // Switch to words tab if phrases tab is active but no expressions after loading
  useEffect(() => {
    if (activeTab === "phrases" && !isLoading && practiceExpressions.length === 0) {
      setActiveTab("words");
    }
  }, [activeTab, isLoading, practiceExpressions.length]);

  // Build array of all phrases (main + alternatives)
  const allPhrases = useMemo(() => {
    const phrases = [{ text: node.text, translation: node.translation }];
    if (node.alternatives) {
      phrases.push(...node.alternatives);
    }
    return phrases;
  }, [node.text, node.translation, node.alternatives]);

  const hasMultiplePhrases = allPhrases.length > 1;

  const goToPrevPhrase = () => {
    setCurrentPhraseIndex((prev) => (prev > 0 ? prev - 1 : allPhrases.length - 1));
  };

  const goToNextPhrase = () => {
    setCurrentPhraseIndex((prev) => (prev < allPhrases.length - 1 ? prev + 1 : 0));
  };

  // Fetch definitions for words and expressions
  const fetchVocab = useCallback(async (
    words: string[],
    sentence: string
  ): Promise<{ words: Map<string, PracticeWord>; expressions: PracticeExpression[]; phoneticTranscription?: string }> => {
    const wordMap = new Map<string, PracticeWord>();
    const expressions: PracticeExpression[] = [];
    let phonetic: string | undefined;

    if (words.length === 0 && !sentence) {
      return { words: wordMap, expressions };
    }

    try {
      const { data } = await api.POST("/api/vocab-lookup", {
        body: {
          words: words,
          sentence: sentence,
          targetLanguage: targetLanguage.code,
          nativeLanguage: nativeLanguage.code,
          knownLanguages: userProfile.knownLanguages,
        },
      });

      if (data?.words) {
        // Backend returns results roughly in order - map by index
        data.words.forEach((entry, idx) => {
          const originalWord = words[idx]?.toLowerCase();
          if (originalWord) {
            wordMap.set(originalWord, {
              word: words[idx]!, // Keep original casing
              baseForm: entry.baseForm,
              translation: entry.translation,
              pronunciation: entry.pronunciation || undefined,
              vocalizationTip: entry.vocalizationTip || undefined,
            });
          }
        });
      }

      if (data?.expressions) {
        for (const expr of data.expressions) {
          expressions.push({
            expression: expr.expression,
            translation: expr.translation,
            literal: expr.literal || undefined,
            components: expr.components?.map((c) => ({
              word: c.word,
              meaning: c.meaning,
            })) || [],
          });
        }
      }

      // Capture phonetic transcription
      phonetic = data?.phoneticTranscription || undefined;
    } catch (err) {
      console.error("Failed to fetch vocab:", err);
    }
    return { words: wordMap, expressions, phoneticTranscription: phonetic };
  }, [targetLanguage.code, nativeLanguage.code, userProfile.knownLanguages]);

  // Current phrase text for vocabulary extraction
  const currentPhraseText = allPhrases[currentPhraseIndex]?.text || node.text;

  // Helper to merge vocab data into final word list
  const mergeVocabData = useCallback((
    allWords: string[],
    matched: PracticeWord[],
    fetchedMap: Map<string, PracticeWord>
  ): PracticeWord[] => {
    const finalWords: PracticeWord[] = [];
    const seen = new Set<string>();

    for (const word of allWords) {
      const normalized = word.toLowerCase();
      if (seen.has(normalized)) continue;
      seen.add(normalized);

      // Check matched first, then fetched
      const matchedWord = matched.find(m => m.word.toLowerCase() === normalized);
      if (matchedWord) {
        finalWords.push(matchedWord);
      } else {
        const fetchedWord = fetchedMap.get(normalized);
        if (fetchedWord) {
          finalWords.push(fetchedWord);
        }
      }
    }
    return finalWords;
  }, []);

  // Extract all words and fetch definitions + expressions for current phrase
  useEffect(() => {
    const loadVocab = async () => {
      const allWords = extractAllWords(currentPhraseText);
      const dict = vocabulary ? buildDictionary(vocabulary) : new Map();
      const { matched, unmatched } = matchWordsToVocab(allWords, dict);

      // Show matched words immediately (greedy)
      setPracticeWords(matched);
      setPracticeExpressions([]);
      setPhoneticTranscription(null);

      // Check if we have prefetched data ready (instant)
      if (prefetchedVocab) {
        const finalWords = mergeVocabData(allWords, matched, prefetchedVocab.words);
        setPracticeWords(finalWords);
        setPracticeExpressions(prefetchedVocab.expressions);
        setPhoneticTranscription(prefetchedVocab.phoneticTranscription || null);
        setIsLoading(false);
        return;
      }

      // Check if prefetch is in progress (await it)
      if (prefetchPromise) {
        setIsLoading(true);
        try {
          const prefetched = await prefetchPromise;
          const finalWords = mergeVocabData(allWords, matched, prefetched.words);
          setPracticeWords(finalWords);
          setPracticeExpressions(prefetched.expressions);
          setPhoneticTranscription(prefetched.phoneticTranscription || null);
          setIsLoading(false);
          return;
        } catch {
          // Fall through to fresh fetch
        }
      }

      // No prefetch available - fetch fresh
      setIsLoading(true);
      const { words: fetchedMap, expressions, phoneticTranscription: phonetic } = await fetchVocab(unmatched, currentPhraseText);
      const finalWords = mergeVocabData(allWords, matched, fetchedMap);
      setPracticeWords(finalWords);
      setPracticeExpressions(expressions);
      setPhoneticTranscription(phonetic || null);
      setIsLoading(false);
    };

    loadVocab();
  }, [currentPhraseText, vocabulary, fetchVocab, prefetchedVocab, prefetchPromise, mergeVocabData]);

  const handleSpeak = (text: string) => {
    if (ttsState === "playing" && speakingText === text) {
      stop();
      setSpeakingText(null);
    } else {
      setSpeakingText(text);
      speak(text);
    }
  };

  const getButtonState = (text: string) => {
    if (speakingText === text) return ttsState;
    return "idle";
  };

  const currentPhrase = allPhrases[currentPhraseIndex] ?? allPhrases[0]!;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative z-10 w-full max-w-md rounded-2xl bg-card border border-border shadow-xl max-h-[85dvh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h2 className="text-lg font-semibold">{t.dialog.practice}</h2>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-muted"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto overscroll-contain p-4 space-y-6">
          {/* Phrase carousel section */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              {/* Left arrow */}
              {hasMultiplePhrases && (
                <button
                  type="button"
                  onClick={goToPrevPhrase}
                  className="p-1.5 rounded-full hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
              )}

              {/* Current phrase */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start gap-3">
                  <div className="flex-1">
                    <p className="text-lg font-medium">{currentPhrase.text}</p>
                    {phoneticTranscription && (
                      <p className="text-sm text-primary/80 mt-0.5 font-mono">
                        {phoneticTranscription}
                      </p>
                    )}
                    <p className="text-sm text-muted-foreground mt-1">
                      {currentPhrase.translation}
                    </p>
                  </div>
                  <SpeakerButton
                    state={getButtonState(currentPhrase.text)}
                    onClick={() => handleSpeak(currentPhrase.text)}
                    className="flex-shrink-0"
                  />
                </div>
              </div>

              {/* Right arrow */}
              {hasMultiplePhrases && (
                <button
                  type="button"
                  onClick={goToNextPhrase}
                  className="p-1.5 rounded-full hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              )}
            </div>

            {/* Dots indicator */}
            {hasMultiplePhrases && (
              <div className="flex justify-center gap-1.5">
                {allPhrases.map((_, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => setCurrentPhraseIndex(idx)}
                    className={cn(
                      "w-2 h-2 rounded-full transition-colors",
                      idx === currentPhraseIndex
                        ? "bg-primary"
                        : "bg-muted-foreground/30 hover:bg-muted-foreground/50"
                    )}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Tabs for Words / Phrases - non-blocking, show content while loading */}
          {(practiceWords.length > 0 || practiceExpressions.length > 0 || isLoading) ? (
            <div className="space-y-3">
              {/* Tab buttons - only show Phrases tab if there are expressions or still loading */}
              {(practiceExpressions.length > 0 || isLoading) ? (
                <div className="flex gap-2 border-b border-border">
                  <button
                    type="button"
                    onClick={() => setActiveTab("words")}
                    className={cn(
                      "px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors",
                      activeTab === "words"
                        ? "border-primary text-primary"
                        : "border-transparent text-muted-foreground hover:text-foreground"
                    )}
                  >
                    {t.dialog.vocabulary} ({practiceWords.length}{isLoading ? "+" : ""})
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveTab("phrases")}
                    className={cn(
                      "px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors",
                      activeTab === "phrases"
                        ? "border-primary text-primary"
                        : "border-transparent text-muted-foreground hover:text-foreground"
                    )}
                  >
                    Phrases ({practiceExpressions.length}{isLoading ? "+" : ""})
                  </button>
                </div>
              ) : (
                /* No expressions and done loading - just show words header */
                <div className="flex gap-2 border-b border-border">
                  <span className="px-3 py-2 text-sm font-medium border-b-2 -mb-px border-primary text-primary">
                    {t.dialog.vocabulary} ({practiceWords.length})
                  </span>
                </div>
              )}

              {/* Words tab content */}
              {activeTab === "words" && practiceWords.length > 0 && (
                <div className="space-y-2">
                  {practiceWords.map((word, idx) => (
                    <div
                      key={`${word.word}-${idx}`}
                      className="flex items-center gap-3 p-3 rounded-lg bg-muted/50"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline gap-2 flex-wrap">
                          <span className="font-medium">{word.baseForm}</span>
                          {word.pronunciation && (
                            <span className="text-xs text-muted-foreground">
                              /{word.pronunciation}/
                            </span>
                          )}
                          {word.vocalizationTip && (
                            <span className="text-xs text-muted-foreground italic">
                              [{word.vocalizationTip}]
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-primary">
                          {word.translation}
                        </p>
                      </div>
                      <SpeakerButton
                        state={getButtonState(word.baseForm)}
                        onClick={() => handleSpeak(word.baseForm)}
                        size="sm"
                      />
                    </div>
                  ))}
                </div>
              )}

              {/* Phrases tab content */}
              {activeTab === "phrases" && practiceExpressions.length > 0 && (
                <div className="space-y-3">
                  {practiceExpressions.map((expr, idx) => (
                    <div
                      key={`${expr.expression}-${idx}`}
                      className="p-3 rounded-lg bg-muted/50 space-y-2"
                    >
                      <div className="flex items-start gap-3">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium">{expr.expression}</p>
                          <p className="text-sm text-primary">
                            {expr.translation}
                          </p>
                          {expr.literal && (
                            <p className="text-xs text-muted-foreground italic mt-1">
                              Literal: {expr.literal}
                            </p>
                          )}
                        </div>
                        <SpeakerButton
                          state={getButtonState(expr.expression)}
                          onClick={() => handleSpeak(expr.expression)}
                          size="sm"
                        />
                      </div>
                      {expr.components.length > 0 && (
                        <div className="pt-2 border-t border-border/50">
                          <p className="text-xs text-muted-foreground mb-1">Components:</p>
                          <div className="flex flex-wrap gap-2">
                            {expr.components.map((comp, cidx) => (
                              <span
                                key={cidx}
                                className="text-xs px-2 py-1 rounded bg-background"
                              >
                                <span className="font-medium">{comp.word}</span>
                                <span className="text-muted-foreground"> = {comp.meaning}</span>
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Empty state or loading for words tab */}
              {activeTab === "words" && practiceWords.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-4">
                  {isLoading ? t.dialog.loadingVocabulary : "No individual words found"}
                </p>
              )}
              {/* Loading state for phrases tab */}
              {activeTab === "phrases" && practiceExpressions.length === 0 && isLoading && (
                <p className="text-sm text-muted-foreground text-center py-4">
                  {t.dialog.loadingVocabulary}
                </p>
              )}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
